import {
  Typography,
  CardActions,
  Button,
  IconButton,
  Theme,
  Box,
  useMediaQuery
} from '@mui/material';
import ReactPlayer from 'react-player';
import { FC, useState, useEffect, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ENewsCategory, ENewsContentType, INews } from '../../models';
import { Card, Loader } from '../../components';
import {
  Campaign,
  FiberNew,
  WarningAmber,
  ExpandLess,
  ExpandMore,
  Download
} from '@mui/icons-material';
import {
  ALLOWED_IMAGE_EXTENSIONS,
  downloadFile,
  formatDate,
  getDownloadFileName
} from '../../helpers';
import { getNewsFile, getNewsVideo, markNewsItemRead } from '../../fetch';
import makeStyles from '@mui/styles/makeStyles';
import { NewsNotificationContext } from '../../context/news-notification';
import { logError } from '../../services';
import { AxiosResponse } from 'axios';

interface INewsCard {
  newsItem: INews;
  showIcon?: boolean;
  isNewsPage?: boolean;
}

interface IFile {
  url: string;
  fileName: string;
}

const getFileExtension = (fileName: string) => {
  return fileName?.split('.')?.pop();
};

const isPDFAttachment = (file: IFile) => getFileExtension(file.fileName) === 'pdf';

export const NewsCard: FC<INewsCard> = ({ newsItem, showIcon = true, isNewsPage = false }) => {
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  const [file, setFile] = useState<IFile>({ url: '', fileName: '' });
  const [videoFile, setVideoFile] = useState<IFile>({ url: '', fileName: '' });
  const [rawFile, setRawFile] = useState<AxiosResponse | null>(null);
  const [pdfURL, setPdfURL] = useState('');
  const classes = useStyles({
    isUnRead: newsItem?.unread ?? true
  });
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [isFetchingVideoFile, setIsFetchingVideoFile] = useState(false);

  const news = useContext(NewsNotificationContext);
  const isMobile = useMediaQuery(`(max-width: 900px)`);

  const fetchFile = async () => {
    try {
      setIsFetchingFile(true);
      const res = await getNewsFile(newsItem?.newsItemId, false);
      if (res.status === 200) {
        setRawFile(res);
        let url = URL.createObjectURL(res?.data);
        setFile({ url, fileName: getDownloadFileName(res) });
      }
    } catch (e) {
      logError(e, 'fetchFile failed');
    } finally {
      setIsFetchingFile(false);
    }
  };
  const fetchVideoFile = async () => {
    try {
      setIsFetchingVideoFile(true);
      const res = await getNewsVideo(newsItem?.newsItemId, false);
      if (res.status === 200) {
        setVideoFile({ url: res.data, fileName: '' });
      }
    } catch (e) {
      logError(e, 'fetchFile failed');
    } finally {
      setIsFetchingVideoFile(false);
    }
  };
  useEffect(() => {
    // only call for attached files and if we haven't already loaded the file
    if (
      newsItem?.newsItemType === ENewsContentType.AttachedFile &&
      isExpanded &&
      !newsItem.isVideo &&
      file.url.length === 0
    ) {
      fetchFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsItem, isExpanded, file]);

  useEffect(() => {
    // only call for attached files and if we haven't already loaded the file
    if (
      newsItem?.newsItemType === ENewsContentType.AttachedFile &&
      isExpanded &&
      newsItem.isVideo &&
      videoFile.url.length === 0
    ) {
      fetchVideoFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsItem, isExpanded, videoFile]);

  const handleExpandClick = () => {
    if (newsItem?.unread) {
      news.getUnreadNewsItemCount();
      newsItem.unread = false;
    }

    // Mark news item as read if it is not expanded
    // call the api everytime we open the accordion
    // non blocking call
    if (!isExpanded) {
      markNewsItemRead(newsItem.newsItemId);
    }

    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (isPDFAttachment(file)) {
      let newBlob = new Blob([rawFile?.data], { type: 'application/pdf' });
      setPdfURL(URL.createObjectURL(newBlob));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, rawFile, isPDFAttachment]);

  const isVideoExternalUrl = useMemo(
    () => newsItem?.externalUrl?.includes('youtube'),
    [newsItem.externalUrl]
  );
  return (
    <Card
      title={
        <>
          <Typography>{newsItem.subject}</Typography>
          <Typography variant="caption">{formatDate(newsItem.dateCreated)}</Typography>
        </>
      }
      isExpanded={isExpanded}
      icon={
        <>
          {newsItem.category === ENewsCategory.Alert && (
            <WarningAmber className={classes.icon} fontSize="medium" />
          )}
          {newsItem.category === ENewsCategory.Announcement && (
            <Campaign className={classes.icon} fontSize="medium" />
          )}
          {newsItem.category === ENewsCategory.Feature && (
            <FiberNew className={classes.icon} fontSize="medium" />
          )}
        </>
      }
      action={
        <IconButton
          onClick={() => handleExpandClick()}
          aria-expanded={isExpanded}
          aria-label="show more"
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      }
      cardHeaderSx={{
        padding: '8px'
      }}
      headerClassName={classes.header}
    >
      <Typography variant="body2">
        <span dangerouslySetInnerHTML={{ __html: newsItem.message }} />
      </Typography>
      {!!newsItem.customPageId && (
        <CardActions sx={{ padding: 0 }}>
          <Button
            onClick={() => history.push(`/custom-pages/${newsItem.customPageSlug}`)}
            color="primary"
            size="small"
          >
            Learn More
          </Button>
        </CardActions>
      )}
      {!!newsItem.externalUrl && !isVideoExternalUrl && (
        <CardActions sx={{ padding: 0 }}>
          <Button
            onClick={() => window.open(newsItem.externalUrl, '_blank')}
            color="primary"
            size="small"
          >
            Learn More
          </Button>
        </CardActions>
      )}
      <CardActions sx={{ padding: 0, flexDirection: 'column' }}>
        {/* image file shown */}
        {newsItem.newsItemType === ENewsContentType.AttachedFile &&
          file?.fileName &&
          ALLOWED_IMAGE_EXTENSIONS.includes(`.${getFileExtension(file.fileName)}`) && (
            <Box sx={{ maxWidth: '500px' }}>
              <Box
                component="img"
                sx={{ width: '100%', height: 'auto', maxWidth: 'fit-content' }}
                alt="News Item"
                src={file.url}
              />
            </Box>
          )}
        {/* video file shown */}
        {(!!videoFile?.url || isVideoExternalUrl) && (
          <Box>
            <ReactPlayer
              url={videoFile?.url || newsItem.externalUrl}
              controls
              playing
              width="100%"
              height="100%"
              onReady={e => {
                setTimeout(() => {
                  const player = e?.getInternalPlayer();
                  player?.setAttribute?.('muted', 'false');
                }, 200);
              }}
            />
          </Box>
        )}
        {isNewsPage && (
          <>
            {/* pdf file uploaded, download when on mobile, show in iframe when not */}
            {isPDFAttachment(file) && (
              <>
                {!isMobile ? (
                  <Box sx={{ width: '100%' }}>
                    <iframe
                      width="100%"
                      height="600"
                      src={pdfURL ?? ''}
                      title="newsItem PDF"
                    ></iframe>
                  </Box>
                ) : (
                  <Button
                    component="a"
                    href={pdfURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    size="small"
                  >
                    View PDF
                  </Button>
                )}
              </>
            )}
          </>
        )}
        {!isPDFAttachment(file) && newsItem.newsItemType === ENewsContentType.AttachedFile && (
          <Button
            component="a"
            onClick={
              isNewsPage
                ? () => downloadFile(rawFile.data, file.fileName)
                : () => history.push(`/news`)
            }
            color="primary"
            size="small"
            startIcon={
              isNewsPage && (isFetchingFile || isFetchingVideoFile) ? (
                <Loader type="inline" />
              ) : isNewsPage && file.fileName ? (
                <Download />
              ) : null
            }
            disabled={isNewsPage && (isFetchingFile || isFetchingVideoFile)}
          >
            {isNewsPage && (isFetchingFile || isFetchingVideoFile || newsItem.isVideo)
              ? ''
              : isNewsPage && file.fileName
              ? 'Download'
              : 'Learn More'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
const useStyles = makeStyles<Theme, { isUnRead: boolean }>((theme: Theme) => ({
  header: {
    backgroundColor: ({ isUnRead }) =>
      isUnRead ? theme.palette.primary.main : theme.palette.common.white,
    color: ({ isUnRead }) => (isUnRead ? theme.palette.common.white : theme.palette.common.black),
    border: ({ isUnRead }) => (isUnRead ? null : `1px solid ${theme.palette.primary.main}`),
    transition: ' background-color 0.5s ease, color 0.5s ease, border 0.8s ease-in-out'
  },
  icon: {
    color: ({ isUnRead }) => (isUnRead ? theme.palette.common.white : theme.palette.primary.main),
    transition: 'color 0.8s ease'
  }
}));
